(() => {
  if (window.__mkoCroRuntimeReady) return;
  window.__mkoCroRuntimeReady = true;

  const STORAGE_KEY = 'mko_cro_attribution_v1';
  const LANDING_KEY = 'mko_cro_landing_sent_v1';
  const ALLOWED_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'fbclid',
    'gclid',
    'ttclid'
  ];

  const state = {
    enabled: false,
    listenersBound: false,
    pageEventsSent: false,
    context: {},
    attribution: {}
  };

  const safeStorage = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (_) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (_) {}
    }
  };

  const parseContext = () => {
    const node = document.getElementById('mko-cro-context');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_) {
      return {};
    }
  };

  const parseStoredJson = (value) => {
    if (!value) return {};
    try {
      return JSON.parse(value);
    } catch (_) {
      return {};
    }
  };

  const captureAttribution = () => {
    const stored = parseStoredJson(safeStorage.get(STORAGE_KEY));
    const params = new URLSearchParams(window.location.search);
    const captured = {};

    ALLOWED_PARAMS.forEach((key) => {
      const value = params.get(key);
      if (value) captured[key] = value.slice(0, 250);
    });

    let referrerHost = '';
    try {
      referrerHost = document.referrer ? new URL(document.referrer).hostname : '';
    } catch (_) {}

    const attribution = {
      ...stored,
      ...captured,
      landing_path: stored.landing_path || window.location.pathname,
      referrer_host: stored.referrer_host || referrerHost,
      first_seen_at: stored.first_seen_at || new Date().toISOString()
    };

    safeStorage.set(STORAGE_KEY, JSON.stringify(attribution));
    return attribution;
  };

  const basePayload = () => ({
    schema_version: 1,
    occurred_at: new Date().toISOString(),
    page_type: state.context.pageType || '',
    template: state.context.template || '',
    path: window.location.pathname,
    currency: state.context.currency || '',
    ...state.attribution
  });

  const publish = (name, detail = {}) => {
    if (!state.enabled || !name) return;

    const payload = {
      ...basePayload(),
      ...detail
    };

    try {
      if (window.Shopify?.analytics?.publish) {
        const result = window.Shopify.analytics.publish(`mko:${name}`, payload);
        if (result && typeof result.catch === 'function') result.catch(() => {});
      }
    } catch (_) {}

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: `mko_${name.replace(/[^a-z0-9_]+/gi, '_')}`,
        ...payload
      });
    } catch (_) {}
  };

  const productPayload = () => {
    const product = state.context.product || {};
    const form = document.querySelector('.main-product form[action*="/cart/add"]');
    const variantInput = form?.querySelector('[name="id"]');
    const quantityInput = form?.querySelector('[name="quantity"]');

    return {
      product_id: product.id || '',
      product_handle: product.handle || '',
      product_vendor: product.vendor || '',
      product_title: product.title || '',
      product_url: product.url || '',
      variant_id: variantInput?.value || product.variantId || '',
      quantity: Number(quantityInput?.value || 1),
      price: product.price ?? null,
      available: product.available ?? null
    };
  };

  const cardPayload = (card, link) => ({
    product_handle:
      card?.dataset?.productHandle ||
      link?.href?.match(/\/products\/([^/?#]+)/)?.[1] ||
      '',
    destination_path: (() => {
      try {
        return link ? new URL(link.href, window.location.origin).pathname : '';
      } catch (_) {
        return '';
      }
    })(),
    collection_handle: state.context.collection?.handle || ''
  });

  const bindInteractions = () => {
    if (state.listenersBound) return;
    state.listenersBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const menuTrigger = target.closest(
          '[aria-controls="menuDrawer"], .js-menu-drawer-trigger, [data-menu-drawer-trigger]'
        );
        if (menuTrigger) publish('menu_open', { location: 'header' });

        const reviewsLink = target.closest(
          '[href="#mko-product-reviews"], .mko-review-summary, .jdgm-preview-badge a'
        );
        if (reviewsLink) publish('reviews_click', productPayload());

        const sizeHelp = target.closest('.mko-pdp-size-help');
        if (sizeHelp) publish('size_help_click', productPayload());

        const codBlock = target.closest('.mko-product-cod-block');
        if (
          codBlock &&
          target.closest('button, a, [role="button"], input[type="submit"]')
        ) {
          publish('cod_click', {
            ...productPayload(),
            purchase_path: 'cod'
          });
          return;
        }

        const dynamicCheckout = target.closest('.shopify-payment-button__button');
        if (dynamicCheckout) {
          publish('dynamic_checkout_click', {
            ...productPayload(),
            purchase_path: 'dynamic_checkout'
          });
          return;
        }

        const addToCart = target.closest(
          '.main-product .product-btn-atc, .main-product button[name="add"], .main-product [data-add-to-cart]'
        );
        if (addToCart) {
          publish('add_to_cart_click', {
            ...productPayload(),
            purchase_path: 'shopify_cart'
          });
          return;
        }

        const productLink = target.closest(
          '.product-card a[href*="/products/"], [data-mko-card] a[href*="/products/"], .mko-collection-card a[href*="/products/"]'
        );
        if (productLink) {
          publish('collection_product_click', cardPayload(productLink.closest(
            '.product-card, [data-mko-card], .mko-collection-card'
          ), productLink));
        }
      },
      { passive: true }
    );

    document.addEventListener(
      'change',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (
          !target ||
          !target.closest('.main-product') ||
          !target.matches(
            'input[name*="option"], select[name*="option"], input[name="id"], select[name="id"]'
          )
        ) {
          return;
        }

        window.requestAnimationFrame(() => {
          publish('variant_selected', {
            ...productPayload(),
            option_name: target.getAttribute('name') || '',
            option_value: target.value || ''
          });
        });
      },
      { passive: true }
    );

    document.addEventListener('submit', (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form) return;

      if (form.matches('form[action*="/search"]')) {
        const query = form.querySelector('input[type="search"], input[name="q"]');
        publish('search_submit', {
          query_length: (query?.value || '').trim().length
        });
      }

      if (
        form.matches('form[action*="/contact"]') &&
        form.closest('.popup, footer, [data-ui-component*="Newsletter"]')
      ) {
        publish('newsletter_submit', {
          location: form.closest('.popup') ? 'popup' : 'footer'
        });
      }
    });
  };

  const sendPageEvents = () => {
    if (state.pageEventsSent) return;
    state.pageEventsSent = true;

    if (!safeStorage.get(LANDING_KEY)) {
      publish('landing_view', {
        landing_page_type: state.context.pageType || ''
      });
      safeStorage.set(LANDING_KEY, '1');
    }

    if (state.context.product) {
      publish('pdp_view', productPayload());
    }
  };

  const enable = () => {
    if (state.enabled) return;
    state.enabled = true;
    state.context = parseContext();
    state.attribution = captureAttribution();
    bindInteractions();
    sendPageEvents();
  };

  const analyticsAllowed = () => {
    try {
      return Boolean(
        window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()
      );
    } catch (_) {
      return false;
    }
  };

  const onConsentReady = () => {
    if (analyticsAllowed()) enable();

    document.addEventListener('visitorConsentCollected', (event) => {
      const detail = event?.detail || {};
      if (detail.analyticsAllowed === true || analyticsAllowed()) enable();
    });
  };

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (!error) onConsentReady();
      }
    );
  } else {
    onConsentReady();
  }

  document.addEventListener('shopify:section:load', () => {
    state.context = parseContext();
    if (state.enabled) bindInteractions();
  });
})();